import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import localeDa from 'air-datepicker/locale/da';
import {DateTime} from 'luxon';

export default function initDateTimePicker(initialPickupDates = []) {

    const DANISH_ZONE = 'Europe/Copenhagen';

    const hourEl = $('#hour');
    const minuteEl = $('#minute');

    let pickupDates = initialPickupDates;
    let processedPickupDates = [];
    let selectedDateStr = null;
    let selectedDateObj = null;
    let currentTime = null;
    let dp = null;

    function setTimeUI(hour, minute) {
        hourEl.text(String(hour).padStart(2, '0'));
        minuteEl.text(String(minute).padStart(2, '0'));
    }

    function timeToDate(dateStr, timeStr) {
        const [h, m] = timeStr.split(':').map(Number);
        return DateTime.fromISO(dateStr, {zone: DANISH_ZONE}).set({hour: h, minute: m, second: 0, millisecond: 0});
    }

    function snapToNext5(date) {
        const remainder = date.minute % 5;
        return remainder === 0 ? date : date.plus({minutes: 5 - remainder}).set({second: 0, millisecond: 0});
    }

    function formatTime(date) {
        return date.toFormat('HH:mm');
    }

    function isWithinRange(date, start, end) {
        return date >= start && date <= end;
    }

    function getValidStartTime(selectedDateStr, selectedDateObj, allDates) {
        if (!selectedDateObj) return null;

        const proposedTime = snapToNext5(timeToDate(selectedDateStr, selectedDateObj.start));
        const endTime = timeToDate(selectedDateStr, selectedDateObj.end);

        if (proposedTime > endTime) {
            const currentIndex = allDates.findIndex(d => d.date === selectedDateStr);
            const nextValid = allDates.slice(currentIndex + 1).find(d => d.isSelectable);
            if (nextValid) {
                return {
                    selectedDateStr: nextValid.date,
                    selectedDateObj: nextValid,
                    currentTime: snapToNext5(timeToDate(nextValid.date, nextValid.start))
                };
            }
            return null;
        }

        return {selectedDateStr, selectedDateObj, currentTime: proposedTime};
    }

    function processDates() {
        const now = DateTime.now().setZone(DANISH_ZONE);
        const todayStr = now.toISODate();

        processedPickupDates = pickupDates.map(d => {
            const isToday = d.date === todayStr;
            const endTime = timeToDate(d.date, d.end);
            const isInTimeRange = isToday ? now <= endTime : true;
            const isSelectable = !d.holiday && isInTimeRange;

            return {
                ...d,
                dateObj: DateTime.fromISO(d.date, {zone: DANISH_ZONE}).toJSDate(),
                isSelectable
            };
        });

        const selectableDatesOnly = processedPickupDates.filter(d => d.isSelectable);
        const minDate = selectableDatesOnly.at(0)?.dateObj || null;
        const maxDate = selectableDatesOnly.at(-1)?.dateObj || null;

        const firstSelectable = selectableDatesOnly[0];
        if (firstSelectable) {
            const resolved = getValidStartTime(firstSelectable.date, firstSelectable, processedPickupDates);
            if (resolved) {
                selectedDateStr = resolved.selectedDateStr;
                selectedDateObj = resolved.selectedDateObj;
                currentTime = resolved.currentTime;
            }
        } else {
            selectedDateStr = todayStr;
            selectedDateObj = null;
            currentTime = now;
        }

        return {minDate, maxDate};
    }

    function updateTimeDisplay() {
        const [hour, minute] = formatTime(currentTime).split(':').map(Number);
        setTimeUI(hour, minute);
    }

    function canAdjustTime(minuteChange = 0, hourChange = 0) {
        const start = timeToDate(selectedDateStr, selectedDateObj.start);
        const end = timeToDate(selectedDateStr, selectedDateObj.end);
        const testTime = currentTime.plus({minutes: minuteChange, hours: hourChange});
        const snapped = snapToNext5(testTime);
        return isWithinRange(snapped, start, end);
    }

    function updateButtonStates() {
        document.querySelector('.h-up').disabled = !canAdjustTime(0, 1);
        document.querySelector('.h-down').disabled = !canAdjustTime(0, -1);
        document.querySelector('.m-up').disabled = !canAdjustTime(5, 0);
        document.querySelector('.m-down').disabled = !canAdjustTime(-5, 0);
    }

    function tryAdjustTime(minuteChange = 0, hourChange = 0) {
        const start = timeToDate(selectedDateStr, selectedDateObj.start);
        const end = timeToDate(selectedDateStr, selectedDateObj.end);
        const newTime = currentTime.plus({minutes: minuteChange, hours: hourChange});
        const snapped = snapToNext5(newTime);

        if (isWithinRange(snapped, start, end)) {
            currentTime = snapped;
            updateUI();
        }
    }

    function updateUI() {
        updateTimeDisplay();
        updateButtonStates();
    }

    function renderCalendar(minDate, maxDate) {
        if (dp) dp.destroy();

        dp = new AirDatepicker('#calendar-container', {
            inline: true,
            timeFormat: 'HH:mm',
            dateFormat: 'dd MMMM yyyy',
            minDate,
            maxDate,
            selectedDates: selectedDateStr ? [DateTime.fromISO(selectedDateStr, {zone: DANISH_ZONE}).toJSDate()] : [],
            toggleSelected: false,
            navTitles: {days: 'MMMM yyyy'},
            locale: localeDa,
            onRenderCell({date, cellType}) {
                if (cellType !== 'day') return;
                const formattedDate = DateTime.fromJSDate(date).toISODate();
                const match = processedPickupDates.find(d => d.date === formattedDate);
                if (!match || !match.isSelectable) {
                    return {disabled: true, classes: 'disabled-date'};
                }
            },
            onSelect({date}) {
                const formattedDate = DateTime.fromJSDate(date).toISODate();
                const newSelected = processedPickupDates.find(d => d.date === formattedDate);
                const resolved = getValidStartTime(formattedDate, newSelected, processedPickupDates);
                if (resolved) {
                    selectedDateStr = resolved.selectedDateStr;
                    selectedDateObj = resolved.selectedDateObj;
                    currentTime = resolved.currentTime;
                    updateUI();
                }
            }
        });
    }

    function setupTimeButtons() {
        document.querySelector('.h-up').addEventListener('click', () => tryAdjustTime(0, 1));
        document.querySelector('.h-down').addEventListener('click', () => tryAdjustTime(0, -1));
        document.querySelector('.m-up').addEventListener('click', () => tryAdjustTime(5, 0));
        document.querySelector('.m-down').addEventListener('click', () => tryAdjustTime(-5, 0));
    }

    // Initial run
    const {minDate, maxDate} = processDates();
    renderCalendar(minDate, maxDate);
    setupTimeButtons();
    updateUI();

    return {
        selectedDateTime: () => `${selectedDateStr} ${formatTime(currentTime)}`,
        updatePickupDates: (newDates) => {
            pickupDates = newDates;
            const {minDate, maxDate} = processDates();
            renderCalendar(minDate, maxDate);
            updateUI();
        }
    }
}
